import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { useManagePlan } from "contexts/ManagePlanContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft } from "@fortawesome/free-solid-svg-icons";
import { NotFound, NotificationBanner, PageGuide } from "components";
import { weekDays } from "utils/constants/onboardingStep";
import { useNavigate, useLocation } from "react-router-dom";
import { MobXProviderContext, observer } from "mobx-react";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { capitalizeFirstLetter, formattedAddres } from "utils/helpers/string";
import dateTime from "utils/helpers/dateTime";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import ConfirmationModal from "components/ConfirmationModal";
import dayjs from "dayjs";

import {
  ChangeContact,
  ChangeDeliveryInstructions,
  ViewBag,
  ChangeMealPlanName,
  ChangeMealDate,
  ChangeDeliveryTimeMealPlan,
  ChangeExpectedOrdesMealPlan,
  ChangeEnabledDaysMealPlan,
  ChangeDeliveryAddressMealPlan,
  ChangeSubsidyMealPlan,
} from "alerts";
import { EditPencil, InfoButton } from "assets/img";
import classNames from "classnames";
import object from "utils/helpers/object";
import { priceFormatter } from "utils/helpers/validation";
import { isEmpty, isPresent } from "utils/helpers/array";
import AddressTimeModal from "components/AddressTimeModal";
import { toJS } from "mobx";

const EditPencilButton = ({ onClick }) => (
  <button onClick={onClick} className="d-row items-center btn-style ml-[5px]">
    <img src={EditPencil} layout="fixed" />
  </button>
);

function UncateringPlans({
  managePlanPress,
  data,
  tzinfo,
  setUncateringMealPlanPending,
}) {
  const dayOrder = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const dayAbbr = {
    monday: "Mon",
    tuesday: "Tue",
    wednesday: "Wed",
    thursday: "Thu",
    friday: "Fri",
    saturday: "Sat",
    sunday: "Sun",
  };

  const renderExpectedBags = (bags) => {
    if (!bags) return null;
    if (typeof bags === "string") {
      return <div>{bags}</div>;
    }

    return (
      <div className="space-y-1">
        {dayOrder
          ?.filter((day) => Number(bags[day]) > 0)
          ?.map((day) => (
            <div key={day} className="text-dark-gray font-inter-medium">
              <span className="inline-block w-[40px]">{dayAbbr[day]}:</span>{" "}
              {bags[day] ?? 0}
            </div>
          ))}
      </div>
    );
  };

  return (
    <div className="mt-8 flow-root">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <table className="min-w-full divide-y divide-gray-300 table-fixed">
            <thead>
              <tr className="bg-light-gray">
                <th
                  scope="col"
                  className="py-3.5 w-1/6 pl-4 pr-3 text-left text-sm font-inter-bold text-dark-gray sm:pl-3"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-3 w-1/6 py-3.5 text-left text-sm font-inter-bold text-dark-gray"
                >
                  Destination
                </th>
                <th
                  scope="col"
                  className="px-3 w-1/6 py-3.5 text-left text-sm font-inter-bold text-dark-gray"
                >
                  Next Order date
                </th>
                <th className="px-3 w-1/6 py-3.5 text-left text-sm font-inter-bold text-dark-gray">
                  Expected order
                </th>
                <th
                  scope="col"
                  className="px-3 w-1/6 py-3.5 text-left text-sm font-inter-bold text-dark-gray"
                >
                  Deadline to order
                </th>
                <th className="w-1/6 relative py-3.5 pl-3 pr-4 sm:pr-3"></th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {data?.map((meal) => (
                <tr
                  key={meal?.id}
                  className="border border-l-0 border-r-0 border-[#DEE2E6] border-b-[0.5px]"
                >
                  <td className="break-words py-4 pl-4 pr-3 text-sm font-inter-medium text-dark-gray sm:pl-3">
                    {capitalizeFirstLetter(meal?.attributes?.name)}
                  </td>

                  <td
                    title={formattedAddres(
                      meal?.attributes?.address?.attributes
                    )}
                    className="px-3 py-4 text-sm font-inter-medium text-dark-gray break-all"
                  >
                    {capitalizeFirstLetter(
                      meal?.attributes?.address?.attributes?.nickname
                    )}{" "}
                    ({meal?.attributes?.address?.attributes?.destination_code})
                  </td>

                  <td className="whitespace-nowrap px-3 py-4 text-sm font-inter-medium text-dark-gray">
                    {meal?.attributes?.next_order_date
                      ? dateTime.formatDate(
                          meal?.attributes?.next_order_date,
                          dateTime.longDateWithWeek
                        )
                      : "-"}
                  </td>

                  <td className="px-3 py-4 text-sm font-inter-medium text-dark-gray break-all">
                    <div className="space-y-1">
                      {renderExpectedBags(meal?.attributes?.expected_bags)}
                    </div>
                  </td>

                  <td className="whitespace-nowrap px-3 py-4 text-sm font-inter-medium text-dark-gray">
                    {dateTime.to12hourformat(meal?.attributes?.cutoff, tzinfo)}
                  </td>

                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm sm:pr-3">
                    <button
                      onClick={() => {
                        if (meal?.attributes?.under_review)
                          setUncateringMealPlanPending(true);
                        else managePlanPress(true, meal?.id);

                        window.scrollTo(0, 0);
                      }}
                      className="btn-outline font-inter-medium border-2"
                    >
                      Manage plan
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UpcomingGroupOrders({ onClick, data, navigate, tzinfo, locationId }) {
  return (
    <div className="mt-8 flow-root">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <table className="min-w-full divide-y divide-gray-300">
            <thead>
              <tr className="bg-light-gray">
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-sm font-inter-bold text-dark-gray sm:pl-3"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-inter-bold text-dark-gray"
                >
                  Destination
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-inter-bold text-dark-gray"
                >
                  Restaurant
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-inter-bold text-dark-gray"
                >
                  Order Date
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-inter-bold text-dark-gray"
                >
                  Delivery Time
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-inter-bold text-dark-gray"
                >
                  Budget per user
                </th>

                <th
                  scope="col"
                  className="relative py-3.5 pl-3 pr-4 sm:pr-3"
                ></th>
              </tr>
            </thead>

            <tbody className="bg-white">
              {data?.map((order) => (
                <tr
                  key={order.id}
                  className="border border-l-0 border-r-0 border-[#DEE2E6] border-b-[0.5px]"
                >
                  <td
                    title={order?.attributes?.meal_name}
                    className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-inter-medium text-dark-gray sm:pl-3"
                  >
                    {order?.attributes?.meal_name?.length > 30
                      ? `${capitalizeFirstLetter(
                          order?.attributes?.meal_name.slice(0, 30)
                        )}...`
                      : capitalizeFirstLetter(order?.attributes?.meal_name)}
                  </td>

                  <td
                    title={formattedAddres(
                      order?.attributes?.address?.attributes
                    )}
                    className="break-all px-3 py-4 text-sm font-inter-medium text-dark-gray"
                  >
                    {capitalizeFirstLetter(
                      order?.attributes?.address?.attributes?.nickname
                    )}{" "}
                    ({order?.attributes?.address?.attributes?.destination_code})
                  </td>

                  <td className="break-all px-3 py-4 text-sm font-inter-medium text-dark-gray">
                    {order?.attributes?.menu_display_name_odc}
                  </td>

                  <td className="whitespace-nowrap px-3 py-4 text-sm font-inter-medium text-dark-gray">
                    {dateTime.formatDate(
                      order?.attributes?.meal_date,
                      dateTime.longDateWithWeek
                    )}
                  </td>

                  <td className="whitespace-nowrap px-3 py-4 text-sm font-inter-medium text-dark-gray">
                    {dateTime.to12hourWithFormat(
                      dateTime?.onlyTime(order?.attributes?.target_time),
                      order?.attributes?.tzinfo,
                      "h:mm A"
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm font-inter-medium text-dark-gray">
                    {priceFormatter(order?.attributes?.budget_per_user || "")}
                  </td>
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm sm:pr-3">
                    <button
                      onClick={() =>
                        navigate(
                          `/group-order/admin/order-review/${order?.attributes?.meal_series_id}/${locationId}`,
                          {
                            state: { from: "/meal-management" },
                          }
                        )
                      }
                      className="btn-outline font-inter-medium border-2"
                    >
                      View order
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!isPresent(data) && (
            <div className="d-col w-full self-center justify-center items-center gap-2">
              <small className="text-secondary font-[14px] mt-[24px]">
                No upcoming group orders
              </small>

              <button
                onClick={() => {
                  navigate("/our-services");
                }}
                className="btn-light-gray"
              >
                Learn about Group Orders
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const UpcomingCateringOrders = ({ data, navigate, locationId }) => {
  return (
    <div className="mt-8 flow-root">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <table className="min-w-full divide-y divide-gray-300">
            <thead>
              <tr className="bg-light-gray">
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-sm font-inter-bold text-dark-gray sm:pl-3"
                >
                  Destination
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-inter-bold text-dark-gray"
                >
                  Restaurant
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-inter-bold text-dark-gray"
                >
                  Order Date
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-inter-bold text-dark-gray"
                >
                  Delivery Time
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-inter-bold text-dark-gray"
                >
                  Total Items
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-inter-bold text-dark-gray"
                ></th>
              </tr>
            </thead>

            <tbody className="bg-white space-y-4">
              {data?.map((meal) => (
                <tr
                  key={meal?.id}
                  className={`border border-l-0 border-r-0 border-[#DEE2E6] border-b-[0.5px] ${
                    meal?.attributes?.declined_at
                      ? "text-[#566884]"
                      : "text-dark-gray"
                  }`}
                >
                  <td
                    title={formattedAddres(
                      meal?.attributes?.address?.attributes
                    )}
                    className="break-words pl-4 py-5 pr-3 text-sm font-inter-medium sm:pl-3"
                  >
                    {capitalizeFirstLetter(
                      meal?.attributes?.address?.attributes?.nickname
                    )}{" "}
                    ({meal?.attributes?.address?.attributes?.destination_code})
                  </td>
                  <td className="whitespace-nowrap px-3 text-sm font-inter-medium">
                    {meal?.attributes?.menu_display_name_odc}
                  </td>
                  <td className="whitespace-nowrap px-3 text-sm font-inter-medium">
                    {dateTime.formatDate(
                      meal?.attributes?.meal_date,
                      dateTime.longDateWithWeek
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3  text-sm font-inter-medium">
                    {dateTime.to12hourWithFormat(
                      dateTime?.onlyTime(meal?.attributes?.target_time),
                      meal?.attributes?.tzinfo,
                      "h:mm A"
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 text-sm font-inter-medium">
                    {meal?.attributes?.items_count}
                  </td>

                  <td className="relative whitespace-nowrap pl-3 pr-4 text-right text-sm sm:pr-3">
                    <td className="relative whitespace-nowrap pl-3 pr-4 text-right text-sm sm:pr-3">
                      <button
                        onClick={() =>
                          navigate(
                            `/meal-management/catering-order/${meal?.attributes?.orders[0]?.slug}/${locationId}`
                          )
                        }
                        className="btn-outline font-inter-medium border-2"
                      >
                        View order
                      </button>
                    </td>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!isPresent(data) && (
            <div className="d-col w-full self-center justify-center items-center gap-2">
              <small className="text-secondary font-[14px] mt-[24px]">
                No upcoming catering orders
              </small>

              <button
                onClick={() => {
                  navigate("/our-services");
                }}
                className="btn-light-gray"
              >
                Learn about Catering
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function ListView() {
  const [viewBagModal, setViewBag] = useState(false);
  const {
    managePlan,
    setManagePlan,
    changeDeliveryTime,
    setChangeDeliveryTime,
    changeExpectedOrders,
    setChangeExpectedOrders,
    changeSubsidy,
    setChangeSubsidy,
    setChangeContact,
    changeContact,
    changeDeliveryInstructions,
    setChangeDeliveryInstructions,
    changeMealPlanName,
    setChangeMealPlanName,
    changeEnabledDays,
    setChangeEnabledDays,
    changeAddress,
    setChangeAddress,
    changeMealDate,
    setChangeMealDate,
  } = useManagePlan();
  const navigate = useNavigate();

  const { mealManagementStore, userStore, deliveryInfoStore } =
    useContext(MobXProviderContext);

  const mealPlans = mealManagementStore.mealPlans;
  const mealPlan = mealManagementStore.mealPlan;
  const groupOrders = mealManagementStore.groupOrders;
  const cateringOrders = mealManagementStore.cateringOrders;
  const defaultLocation = userStore.defaultLocation;
  const mealPlansPagy = mealManagementStore.mealPlansPagy;
  const groupOrdersPagy = mealManagementStore.groupOrdersPagy;
  const cateringOrdersPagy = mealManagementStore.cateringOrdersPagy;

  const [recurringMeal, setRecurringMeal] = useState(false);
  const [addressTimeModal, setAddressTimeModal] = useState(false);
  const [mealType, setMealType] = useState("");
  const [expandUncatering, setExpandUncatering] = useState(false);
  const [expandCatering, setExpandCatering] = useState(false);
  const [uncateringMealPlanPending, setUncateringMealPlanPending] =
    useState(false);

  const enabledDays = useMemo(
    () => mealPlan && mealPlan?.attributes?.days?.map((day) => day),
    [mealPlan]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchMealData = useCallback(() => {
    if (defaultLocation?.organizationId && !managePlan) {
      deliveryInfoStore?.getLocationAddresses({
        location_id: defaultLocation?.organizationId,
      });

      mealManagementStore
        .fetchMealPlans(defaultLocation?.organizationId, {
          service_eq: "uncatering",
          per_page: 10,
        })
        .then(() =>
          mealManagementStore.fetchGroupOrders(
            defaultLocation?.organizationId,
            { per_page: 10 }
          )
        )
        .then(() =>
          mealManagementStore.fetchCateringOrders(
            defaultLocation?.organizationId,
            { per_page: 10 }
          )
        );
    }
  }, [
    defaultLocation?.organizationId,
    managePlan,
    deliveryInfoStore,
    mealManagementStore,
  ]);

  useEffect(() => {
    fetchMealData();
  }, [fetchMealData]);

  const managePlanPress = (value, id) => {
    mealManagementStore.fetchMealPlan(defaultLocation?.organizationId, id);
    setManagePlan(value);
  };

  const getRequestStatus = (targetMapping) => {
    if (isPresent(mealPlan?.attributes?.change_requests)) {
      let request = [...mealPlan?.attributes?.change_requests]
        .reverse() // Reverse the array to traverse from last to first
        ?.find((change) => change?.attributes?.target_mapping == targetMapping);

      if (request) {
        if (targetMapping == "delivery_time")
          return `Requested update: ${request?.attributes?.change_request_items?.target_time?.new_value} - under review`;
        else if (targetMapping == "expected_bags") {
          return `Requested update: ${request?.attributes?.change_request_items?.expected_bags?.new_value} - under review`;
        } else if (targetMapping == "subsidy") {
          let subsidyType = request?.attributes?.change_request_items
            ?.subsidy_type
            ? request?.attributes?.change_request_items?.subsidy_type?.new_value
            : request?.attributes?.change_request_items?.daily_subsidy
            ? "daily"
            : "weekly";

          let text = "";
          if (subsidyType == "daily") {
            let newVal =
              request?.attributes?.change_request_items?.daily_subsidy
                ?.new_value;
            text = `Daily - Mon ${priceFormatter(
              newVal?.monday
            )}  Tue ${priceFormatter(newVal?.tuesday)}  Wed ${priceFormatter(
              newVal?.wednesday
            )} Thu ${priceFormatter(newVal?.thursday)}  Fri ${priceFormatter(
              newVal?.friday
            )}  Sat ${priceFormatter(newVal?.saturday)}  Sun ${priceFormatter(
              newVal?.sunday
            )}`;
          } else
            text = `Weekly ${priceFormatter(
              request?.attributes?.change_request_items?.subsidy?.new_value
            )}`;
          return `Requested update: ${text} - under review`;
        } else if (targetMapping == "delivery_address") {
          return `Requested update: ${formattedAddres(
            request?.attributes?.change_request_items?.address_id?.new_value
          )} - under review`;
        } else if (targetMapping == "days") {
          let days = request?.attributes?.change_request_items?.days
            ? request?.attributes?.change_request_items?.days?.new_value
            : request?.attributes?.change_request_items?.dates_to_add
                ?.new_value;

          let capitalizedDays = days?.map(
            (day) => day?.charAt(0).toUpperCase() + day?.slice(1)
          );

          let text = `Days: ${capitalizedDays?.join(", ")}`;

          return `Requested update: ${text} - under review`;
        } else if (targetMapping == "dates_to_add") {
          let dates =
            request?.attributes?.change_request_items?.dates_to_add?.new_value;
          let text = `Days: ${dates?.map((date) => date).join(",  ")}`;
          return text;
        }
      }
    }
    return "";
  };

  const handleNextClick = (mealType) => {
    if (mealType === "meal_plans") {
      mealManagementStore.fetchMealPlans(defaultLocation?.organizationId, {
        service_eq: "uncatering",
        per_page: 10,
        page: mealPlansPagy?.page + 1,
      });
    } else if (mealType === "group_orders") {
      mealManagementStore.fetchGroupOrders(defaultLocation?.organizationId, {
        per_page: 10,
        page: groupOrdersPagy?.page + 1,
      });
    } else if (mealType === "catering_orders") {
      mealManagementStore.fetchCateringOrders(defaultLocation?.organizationId, {
        per_page: 10,
        page: cateringOrdersPagy?.page + 1,
      });
    }
  };

  return (
    <div className="rounded-lg flex flex-col min-w-full">
      {managePlan ? (
        <div className="pt-[24px]">
          <NotificationBanner
            message={`Request is under review by hungerhub, this message will disappear once changes have been updated.`}
            messageType="warning"
            visible={mealPlan?.attributes?.changes_under_review}
            abilityToDismiss={false}
            hideClose={true}
            messageLink="Contact us"
            handleLinkClick={() => userStore.setFreshChatOpen(true)}
          />
          <button
            className="flex items-center cursor-pointer mb-[16px]"
            onClick={() => setManagePlan(false)}
          >
            <FontAwesomeIcon
              icon={faAngleLeft}
              style={{ width: 14, height: 14 }}
            />
            <span className="text-md text-left font-inter-medium text-black-light ml-3">
              Back
            </span>
          </button>

          <div className="d-row justify-between">
            <h2 className="font-inter-semibold text-[32px] text-dark-gray">
              Manage Meal Plan
            </h2>

            <PageGuide />
          </div>

          <div className="mt-[16px] bg-[#F4F4F4] px-[10px] py-[13px] rounded-lg inline-flex w-full">
            <img
              src={InfoButton}
              alt="info-icon"
              className="w-[20px] h-[20px] mr-2"
            />
            <p className="font-inter-medium text-dark-gray text-[14px]">
              The changes made here will apply to all future meal orders in your
              meal plan. To adjust a specific meal order, please go to request
              changes for a single day.
            </p>
          </div>
          <div className="d-row mt-[24px] items-center">
            <strong className="text-dark-gray text-[24px] font-inter-medium">
              {mealPlan?.attributes?.name}
            </strong>

            <EditPencilButton
              onClick={() => {
                setChangeMealPlanName(true);
              }}
            />
          </div>

          {isPresent(mealPlan?.attributes?.meals) && (
            <div className="d-row items-center justify-between my-[16px]">
              <small className="text-[#575BE8] font-inter-medium text-[16px] ">
                Manage the next upcoming order in meal plan:{" "}
                {dayjs(mealPlan?.attributes?.meals[0]?.meal_date).format(
                  "MMMM D"
                )}
              </small>

              <button
                className="btn-outline"
                onClick={() => {
                  setChangeMealDate(true);
                }}
              >
                Request changes for a single day
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-6">
            <div className="h-[182px] bg-light-gray w-full rounded-lg p-[24px] d-col">
              <span className="text-dark-gray font-inter-regular text-[16px]">
                Delivery time
              </span>

              <strong className="font-inter-semibold text-[16px] text-dark-gray">
                {dateTime.to12hourWithFormat(
                  mealPlan?.attributes?.target_time,
                  mealPlan?.attributes?.tzinfo,
                  "h:mm A"
                )}
              </strong>

              <p className="text-dark-gray font-inter-medium text-[14px]">
                Please allow for a 15 minute window before/after requested time
              </p>

              {mealPlan?.attributes?.changes_under_review &&
                getRequestStatus("delivery_time") && (
                  <small className="text-[#566985] d-row items-center text-[14px] font-inter-medium">
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      color="#E39F55"
                      size="sm"
                      style={{ width: 14, height: 14 }}
                      className="small-icon mr-2"
                      data-class="my-tooltip"
                    />
                    {getRequestStatus("delivery_time")}
                  </small>
                )}
              <button
                className="btn-white p-[10px] w-[141px] mt-[12px] font-inter-medium text-[12px]"
                onClick={() => setChangeDeliveryTime(true)}
              >
                Request Change
              </button>
            </div>

            <div className="h-[182px] bg-light-gray w-full rounded-lg p-[24px] d-col">
              <span className="text-dark-gray font-inter-regular text-[16px]">
                Deadline to order
              </span>
              <strong className="font-inter-semibold text-[16px] text-dark-gray">
                {dateTime.formatTimeWithFormat(
                  mealPlan?.attributes?.cutoff,
                  "h:mm A",
                  mealPlan?.attributes?.tzinfo
                )}
              </strong>
              <p className="text-dark-gray font-inter-medium text-[14px]">
                Deadline to order is set by hungerhub
              </p>
            </div>

            <div className="min-h-[182px] bg-light-gray w-full rounded-lg p-[24px] d-col">
              <span className="text-dark-gray font-inter-regular text-[16px]">
                Expected Order{" "}
              </span>

              {mealPlan.attributes?.expected_bags_calculation_mode ==
              "same_day_average" ? (
                <div className="grid grid-cols-3 gap-2">
                  {object.isPresent(
                    mealPlan?.attributes?.parsed_expected_bags
                  ) &&
                    Object.entries(mealPlan?.attributes?.parsed_expected_bags)
                      ?.filter(([_, value]) => Number(value) > 0)
                      ?.map((key) => (
                        <div className="bg-white capitalize px-[16px] py-[12px] d-col items-center rounded-3xl">
                          <span className="mr-2 font-inter-medium text-dark-gray text-[14px]">
                            {key[0]}: {key[1]}
                          </span>
                        </div>
                      ))}
                </div>
              ) : (
                <strong className="font-inter-semibold text-[16px] text-dark-gray">
                  {mealPlan.attributes?.expected_bags}
                </strong>
              )}
              <p className="text-dark-gray font-inter-medium text-[14px]">
                The number of people expected to place an order
              </p>

              {mealPlan?.attributes?.changes_under_review &&
                getRequestStatus("expected_bags") && (
                  <small className="text-[#566985] break-all d-row items-center text-[14px] font-inter-medium">
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      color="#E39F55"
                      size="sm"
                      style={{ width: 14, height: 14 }}
                      className="small-icon mr-2"
                      data-class="my-tooltip"
                    />
                    {getRequestStatus("expected_bags")}
                  </small>
                )}

              <button
                className="btn-white p-[10px] w-[141px] mt-[12px] font-inter-medium text-[12px]"
                onClick={() => setChangeExpectedOrders(true)}
              >
                Request Change
              </button>
            </div>

            <div className="bg-light-gray w-full rounded-lg p-[24px] d-col">
              <span className="text-dark-gray font-inter-regular text-[16px] mb-2">
                Recurring Weekly Meals
              </span>

              <div className="mb-0.5 w-full xs:max-w-[215px]">
                {isPresent(mealPlan?.attributes?.days) ? (
                  <div className="flex flex-wrap">
                    {weekDays
                      .filter((value) =>
                        mealPlan?.attributes?.days.includes(
                          value?.toLowerCase()
                        )
                      )
                      .map((value, index) => (
                        <div
                          key={index}
                          className="mr-3 mt-[6px] px-4 py-3 text-slate-700 font-inter-medium text-sm leading-[14px] rounded-3xl justify-center gap-2 flex bg-white text-dark-gray"
                        >
                          {capitalizeFirstLetter(value.slice(0, 3))}
                        </div>
                      ))}
                  </div>
                ) : (
                  <div class="justify-start text-slate-500 text-base font-inter-normal italic leading-none">
                    No recurring weekly meals have been added yet.
                  </div>
                )}
              </div>

              {mealPlan?.attributes?.changes_under_review &&
                getRequestStatus("days") && (
                  <small className="text-[#566985] d-row items-center text-[14px] font-inter-medium mt-2">
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      color="#E39F55"
                      size="sm"
                      style={{ width: 14, height: 14 }}
                      className="small-icon mr-2"
                      data-class="my-tooltip"
                    />
                    {getRequestStatus("days")}
                  </small>
                )}

              <button
                className="btn-white p-[10px] w-[141px] mt-[12px] font-inter-medium text-[12px]"
                onClick={() => {
                  setChangeEnabledDays(true);
                  setRecurringMeal(true);
                }}
              >
                Request Change
              </button>

              <span className="text-dark-gray font-inter-regular text-[16px] mt-6 mb-2">
                One-Time Meals
              </span>

              <div className="mb-0.5 w-full xs:max-w-[215px]">
                {isPresent(mealPlan?.attributes?.dates_to_add) ? (
                  <div className="flex flex-wrap">
                    {mealPlan?.attributes?.dates_to_add
                      ?.slice(0, 5)
                      ?.map((value, index) => {
                        const formatted = dayjs(value).format("MMMM DD");

                        return (
                          <div
                            key={index}
                            className="mr-3 mt-[6px] px-4 py-3 text-slate-700 font-inter-medium text-sm leading-[14px] rounded-3xl justify-center gap-2 flex bg-white text-dark-gray"
                          >
                            {formatted}
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <div class="justify-start text-slate-500 text-base font-inter-normal italic leading-none">
                    No one-time meals have been added yet.
                  </div>
                )}
              </div>

              {mealPlan?.attributes?.changes_under_review &&
                getRequestStatus("dates_to_add") && (
                  <small className="text-[#566985] d-row items-center text-[14px] font-inter-medium mt-2">
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      color="#E39F55"
                      size="sm"
                      style={{ width: 14, height: 14 }}
                      className="small-icon mr-2"
                      data-class="my-tooltip"
                    />
                    {getRequestStatus("dates_to_add")}
                  </small>
                )}

              <button
                className="btn-white p-[10px] w-[141px] mt-[12px] font-inter-medium text-[12px]"
                onClick={() => {
                  setChangeEnabledDays(true);
                  setRecurringMeal(false);
                }}
              >
                Request Change
              </button>
            </div>

            <div className="bg-light-gray rounded-lg p-[24px] d-col">
              <span className="text-dark-gray font-inter-regular text-[16px]">
                Meal subsidy
              </span>

              {mealPlan?.attributes?.standard_subsidy && (
                <div className=" capitalize py-[12px]">
                  <span className="bg-white px-[12px] rounded-2xl py-[12px] font-inter-medium text-[16px] text-dark-gray">
                    {"Default Subsidy " +
                      priceFormatter(mealPlan?.attributes?.standard_subsidy)}
                  </span>
                </div>
              )}

              <span className="font-inter-medium text-dark-gray mt-2">
                {mealPlan?.attributes?.subsidy_type == "weekly" ? (
                  <div className="capitalize py-[12px]">
                    <span className="bg-white px-[12px] rounded-lg py-[12px] font-inter-medium text-[16px] text-dark-gray">
                      {"Weekly " +
                        priceFormatter(mealPlan?.attributes?.subsidy)}
                    </span>
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {enabledDays?.map((day) => (
                      <div
                        key={day}
                        className="bg-white capitalize px-[16px] py-[12px] d-row rounded-2xl"
                      >
                        <small className="mr-2 font-inter-medium text-dark-gray text-[14px]">
                          {day.slice(0, 3)}:
                        </small>

                        <small className="font-inter-medium text-dark-gray text-[14px]">
                          {priceFormatter(
                            mealPlan.attributes?.daily_subsidy?.[day] ?? 0
                          )}
                        </small>
                      </div>
                    ))}
                  </div>
                )}
              </span>

              <div className="d-col mt-2">
                <strong className="text-dark-gray font-inter-semibold tex-[16px]">
                  Overpayment{" "}
                  {mealPlan?.attributes?.employee_pays_by_credit_card
                    ? "enabled"
                    : "disabled"}
                </strong>
                <small className="text-secondary font-inter-medium text-[14px]">
                  Allows individuals to charge their personal credit cards if
                  they go over allotted subsidy
                </small>
              </div>

              {mealPlan?.attributes?.changes_under_review &&
                getRequestStatus("subsidy") && (
                  <small className="text-[#566985] d-row items-center text-[14px] font-inter-medium">
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      color="#E39F55"
                      size="sm"
                      style={{ width: 14, height: 14 }}
                      className="small-icon mr-2"
                      data-class="my-tooltip"
                    />
                    {getRequestStatus("subsidy")}
                  </small>
                )}

              <button
                className="btn-white p-[10px] w-[141px] mt-[12px] font-inter-medium text-[12px]"
                onClick={() => setChangeSubsidy(true)}
              >
                Update
              </button>
            </div>

            <div className="bg-light-gray rounded-lg p-[24px] d-col">
              <div className="d-row justify-between items-center">
                <span className="text-dark-gray font-inter-regular text-[16px]">
                  Teams and Users
                </span>

                <span className="text-dark-gray font-inter-regular text-[16px]">
                  {mealPlan?.attributes?.employee_meal_plans?.length} users in
                  total
                </span>
              </div>

              {isEmpty(mealPlan?.attributes?.team_meal_plans) && (
                <strong className="font-inter-semibold text-dark-gray mb-[6px]">
                  No Teams Assigned
                </strong>
              )}

              {mealPlan?.attributes?.team_meal_plans?.map((team) => {
                return (
                  <strong className="font-inter-semibold text-dark-gray mb-[6px]">
                    {team?.team_name} ({team?.number_of_users + " users"})
                  </strong>
                );
              })}

              {isPresent(
                mealPlan?.attributes?.employee_meal_plans?.filter(
                  (employee) =>
                    employee?.creation_source == "individual_assignment"
                )
              ) && (
                <strong className="font-inter-semibold text-dark-gray text-[16px]">
                  +
                  {mealPlan?.attributes?.employee_meal_plans?.filter(
                    (employee) =>
                      employee?.creation_source == "individual_assignment"
                  )?.length + " other users"}
                </strong>
              )}

              <button
                className="btn-white p-[10px] w-[301px] mt-[12px] font-inter-medium text-[12px]"
                onClick={() => {
                  navigate(`/meal-management/manage-teams/${mealPlan?.id}`);
                }}
              >
                Manage teams and users in this meal plan
              </button>
            </div>

            <div className="p-[16px] bg-light-gray rounded-lg w-full items-start d-col">
              <span className="text-dark-gray font-inter-regular text-[16px]">
                Delivery address details and point of contact
              </span>

              <small className="font-inter-semibold text-[16px] text-dark-gray">
                Delivery address
              </small>
              <small className="font-inter-medium text-[14px] text-dark-gray">
                {formattedAddres(mealPlan?.attributes?.address?.attributes)}
              </small>

              <small className="font-inter-semibold mt-[8px] text-dark-gray text-[16px]">
                Delivery instructions
              </small>
              <small className="font-inter-medium text-[14px] text-dark-gray">
                {
                  mealPlan?.attributes?.address?.attributes
                    ?.delivery_instructions
                }
              </small>

              <small className="font-inter-semibold mt-[8px] text-dark-gray text-[16px]">
                Point of contact
              </small>
              <small className="font-inter-medium text-[14px] text-dark-gray">
                {
                  mealPlan?.attributes?.address?.attributes
                    ?.delivery_contact_name
                }
              </small>

              {mealPlan?.attributes?.changes_under_review &&
                getRequestStatus("delivery_address") && (
                  <small className="text-[#566985] d-row items-center text-[14px] font-inter-medium">
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      color="#E39F55"
                      size="sm"
                      style={{ width: 14, height: 14 }}
                      className="small-icon mr-2"
                      data-class="my-tooltip"
                    />
                    {getRequestStatus("delivery_address")}
                  </small>
                )}

              <button
                onClick={() => {
                  setChangeAddress(true);
                }}
                className="btn-white p-[10px] w-[141px] mt-[12px] font-inter-medium text-[12px]"
              >
                Request Change
              </button>
            </div>
          </div>

          {changeDeliveryTime && (
            <ChangeDeliveryTimeMealPlan
              visible={changeDeliveryTime}
              setModal={setChangeDeliveryTime}
              time={mealPlan?.attributes?.target_time}
              tzinfo={mealPlan?.attributes?.tzinfo}
              meals={mealPlan?.attributes?.meals}
            />
          )}

          {changeExpectedOrders && (
            <ChangeExpectedOrdesMealPlan
              visible={changeExpectedOrders}
              setModal={setChangeExpectedOrders}
              tzinfo={mealPlan?.attributes?.tzinfo}
              meals={mealPlan?.attributes?.meals}
              expectedOrders={mealPlan?.attributes?.expected_bags}
              calculationMode={
                mealPlan.attributes?.expected_bags_calculation_mode
              }
              parsed_expected_bags={mealPlan?.attributes?.parsed_expected_bags}
            />
          )}

          {changeSubsidy && (
            <ChangeSubsidyMealPlan
              visible={changeSubsidy}
              setModal={setChangeSubsidy}
              meals={mealPlan?.attributes?.meals}
            />
          )}
          {changeContact && (
            <ChangeContact
              visible={changeContact}
              setModal={setChangeContact}
            />
          )}
          {changeDeliveryInstructions && (
            <ChangeDeliveryInstructions
              visible={changeDeliveryInstructions}
              setModal={setChangeDeliveryInstructions}
            />
          )}
          {changeMealPlanName && (
            <ChangeMealPlanName
              visible={changeMealPlanName}
              setModal={setChangeMealPlanName}
              placeholder={mealPlan?.attributes?.name}
            />
          )}
          {changeEnabledDays && (
            <ChangeEnabledDaysMealPlan
              visible={changeEnabledDays}
              setModal={setChangeEnabledDays}
              meals={mealPlan?.attributes?.meals}
              days={mealPlan?.attributes?.days}
              dates={mealPlan?.attributes?.dates_to_add}
              recurringMeals={recurringMeal}
            />
          )}
          {changeAddress && (
            <ChangeDeliveryAddressMealPlan
              visible={changeAddress}
              setModal={setChangeAddress}
              mealPlan={mealPlan}
              meals={mealPlan?.attributes?.meals}
            />
          )}
          {changeMealDate && (
            <ChangeMealDate
              visible={changeMealDate}
              setModal={setChangeMealDate}
              meals={mealPlan?.attributes?.meals}
            />
          )}
        </div>
      ) : (
        <>
          <div className="d-row justify-between">
            <div className="d-col">
              <strong className="text-uncatering text-[20px] font-inter-medium">
                Active Uncatering plans
              </strong>
              <small className="font-inter-regular">
                Custom meals ordered by individual employees
              </small>
            </div>

            <div className="flex items-end">
              <button
                className={classNames({
                  "d-row items-center rounded-xl text-catering hover:text-white font-inter-bold": true,
                  "btn-blue-accent text-white": true,
                })}
                onClick={() => {
                  navigate("/get-started-uncatering");
                }}
              >
                + New Uncatering Plan
              </button>
            </div>
          </div>

          {isPresent(mealPlans) ? (
            <div>
              <UncateringPlans
                data={mealPlans}
                managePlanPress={(value, id) => managePlanPress(value, id)}
                tzinfo={defaultLocation?.tzinfo}
                setUncateringMealPlanPending={setUncateringMealPlanPending}
              />

              {mealPlansPagy?.next && (
                <div className="d-row justify-center w-full">
                  <button
                    onClick={() => {
                      handleNextClick("meal_plans");

                      window.scroll({ top: 0, left: 0, behavior: "smooth" });
                    }}
                    className="text-catering d-row items-center font-inter-medium text-[16px] mt-[18px]"
                  >
                    Show More Meal Plans
                    <FontAwesomeIcon
                      icon={expandUncatering ? faChevronLeft : faChevronRight}
                      style={{
                        color: "#575BE8",
                      }}
                      className="transform-left ml-2 transform rotate-90"
                    />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="d-col w-full items-center">
              <NotFound
                data={mealPlans}
                message="No active meal plans enabled"
              />
              <button
                className="btn-disabled-mode"
                onClick={() => {
                  navigate("/our-services");
                }}
              >
                Learn about Uncatering
              </button>
            </div>
          )}

          <div className="d-row justify-between">
            <div className="d-col mt-[32px]">
              <strong className="text-group-order text-[20px] font-inter-medium">
                Upcoming Group Orders
              </strong>
              <small className="font-inter-regular">
                One-off custom meals ordered by individuals
              </small>
            </div>

            <div className="flex items-end">
              <button
                className={classNames({
                  "d-row items-center rounded-xl text-catering hover:text-white font-inter-bold": true,
                  "btn-blue-accent text-white": true,
                })}
                onClick={() => {
                  setMealType("Group");
                  setAddressTimeModal(true);
                }}
              >
                + New Group Order
              </button>
            </div>
          </div>

          <div>
            <UpcomingGroupOrders
              onClick={setViewBag}
              data={groupOrders}
              navigate={navigate}
              tzinfo={defaultLocation?.tzinfo}
              locationId={defaultLocation?.organizationId}
              handleNextClick={handleNextClick}
            />

            {groupOrdersPagy?.next && (
              <div className="d-row justify-center w-full">
                <button
                  onClick={() => {
                    handleNextClick("group_orders");
                  }}
                  className="text-catering d-row items-center font-inter-medium text-[16px] mt-[18px]"
                >
                  Show More Group Orders
                  <FontAwesomeIcon
                    icon={expandUncatering ? faChevronLeft : faChevronRight}
                    style={{
                      color: "#575BE8",
                    }}
                    className="transform-left ml-2 transform rotate-90"
                  />
                </button>
              </div>
            )}
          </div>

          <div className="d-row justify-between">
            <div className="d-col mt-[32px]">
              <strong className="text-catering text-[20px] font-inter-medium">
                Upcoming Catering Orders
              </strong>
              <small className="font-inter-regular">
                Catering order placed by one person on behalf of many
              </small>
            </div>

            <div className="flex items-end">
              <button
                className={classNames({
                  "d-row items-center rounded-xl text-catering hover:text-white font-inter-bold": true,
                  "btn-blue-accent text-white": true,
                })}
                onClick={() => {
                  setMealType("Catering");
                  setAddressTimeModal(true);
                }}
              >
                + New Catering Order
              </button>
            </div>
          </div>

          <UpcomingCateringOrders
            onClick={setViewBag}
            data={
              cateringOrders?.length > 5 && !expandCatering
                ? cateringOrders?.slice(0, 5)
                : cateringOrders
            }
            navigate={navigate}
            locationId={defaultLocation?.organizationId}
            handleNextClick={handleNextClick}
          />

          {cateringOrdersPagy?.next && (
            <div className="d-row justify-center w-full">
              <button
                onClick={() => {
                  handleNextClick("catering_orders");
                }}
                className="text-catering d-row items-center font-inter-medium text-[16px] mt-[18px]"
              >
                Show More Catering Orders
                <FontAwesomeIcon
                  icon={expandCatering ? faChevronLeft : faChevronRight}
                  style={{
                    color: "#575BE8",
                  }}
                  className="transform-left ml-2 transform rotate-90"
                />
              </button>
            </div>
          )}

          {viewBagModal && (
            <ViewBag visible={viewBagModal} setModal={setViewBag} />
          )}

          {addressTimeModal && (
            <AddressTimeModal
              visibleModal={addressTimeModal}
              showModal={setAddressTimeModal}
              orderType={mealType}
            />
          )}

          {uncateringMealPlanPending && (
            <ConfirmationModal
              visible={uncateringMealPlanPending}
              setModal={setUncateringMealPlanPending}
              title="Uncatering request under review"
              body="You will be able to manage your uncatering meal plan as soon as we enable it."
              rightButtonText="Learn more about our services"
              onClickRightButton={() => navigate("/our-services")}
              hideCancel={true}
            />
          )}
        </>
      )}
    </div>
  );
}

export default observer(ListView);
